import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import '../../styles/sitePages.css';
import './AppDetailPage.css';
import { appStoreUrl, playStoreUrl, trackStoreClick } from '../../utils/storeLinks';

function BinarySudokuAppPage() {
  const reviews = [
    {
      author: 'Puzzle Fan',
      rating: '★★★★★',
      text: "Perfect balance of challenge and fun. The rules are simple but the puzzles get surprisingly deep. Love it!",
      source: 'Google Play'
    },
    {
      author: 'Logic Lover',
      rating: '★★★★★',
      text: "Finally a binary puzzle game that's well designed. No ads interrupting gameplay and the difficulty curve is perfect.",
      source: 'App Store'
    },
    {
      author: 'Brain Trainer',
      rating: '★★★★☆',
      text: "Great for quick mental workouts. The larger grids are genuinely challenging. Would recommend to any puzzle enthusiast.",
      source: 'Google Play'
    }
  ];

  const features = [
    { title: '1,300+ Levels', description: 'Hand-crafted puzzles across multiple grid sizes from 6x6 to 14x14.' },
    { title: 'Simple Rules', description: 'Fill the grid with 0s and 1s: no three in a row, balanced rows and columns.' },
    { title: 'No Guessing', description: 'Every puzzle is solvable through pure logic - no trial and error needed.' },
    { title: 'Progressive Difficulty', description: 'Start easy and work your way up to expert-level challenges.' },
    { title: 'Clean Interface', description: 'Minimal, distraction-free design that lets you focus on solving.' },
    { title: 'Offline Play', description: 'No internet required - perfect for commutes and travel.' }
  ];

  return (
    <div className="app-detail-page">
      <Helmet>
        <title>Binary Sudoku+ - Mobile App for iOS & Android | Logic Puzzles</title>
        <meta name="description" content="Download Binary Sudoku+ for iOS and Android. Over 25K downloads with 4.7-star rating. 1,300+ levels of pure binary logic puzzles." />
        <link rel="canonical" href="https://logicpuzzles.ca/apps/binarysudokuplus" />
        <meta property="og:title" content="Binary Sudoku+ - Mobile App for iOS & Android" />
        <meta property="og:description" content="Download Binary Sudoku+. Over 25K downloads with 4.7-star rating. 1,300+ levels of binary logic puzzles." />
        <meta property="og:url" content="https://logicpuzzles.ca/apps/binarysudokuplus" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Binary Sudoku+ - Mobile App for iOS & Android" />
        <meta name="twitter:description" content="Download Binary Sudoku+. Over 25K downloads with 4.7-star rating. 1,300+ levels of binary logic puzzles." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/og-image.png" />
      </Helmet>

      <NavBar />

      <section className="app-detail-hero">
        <div className="container app-detail-hero-content">
          <div className="app-detail-hero-copy">
            <span className="eyebrow">Mobile app · iOS &amp; Android</span>
            <h1>Binary Sudoku+</h1>
            <p className="app-tagline">Clean, fair, binary logic</p>

            <div className="app-detail-stats">
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">4.7 ★</span>
                <span className="app-detail-stat-label">Rating</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">25K+</span>
                <span className="app-detail-stat-label">Downloads</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">1,300+</span>
                <span className="app-detail-stat-label">Levels</span>
              </div>
            </div>

            <div className="app-detail-buttons">
              <a href={appStoreUrl('bsp', 'apps-page', '/apps/binarysudokuplus')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'bsp', store: 'app_store', placement: 'apps-page', page: '/apps/binarysudokuplus' })}>
                <img src="/app-store-badge.svg" alt="Download on the App Store" />
              </a>
              <a href={playStoreUrl('bsp', 'apps-page', '/apps/binarysudokuplus')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'bsp', store: 'google_play', placement: 'apps-page', page: '/apps/binarysudokuplus' })}>
                <img src="/play-store-badge.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
          <div className="app-detail-hero-art">
            <img
              src="/assets/logos/symmetrix_logo.webp"
              alt="Binary Sudoku+ App Icon"
              className="app-detail-icon"
              width="200"
              height="200"
            />
          </div>
        </div>
      </section>

      <div className="app-detail-content">
        <section className="app-detail-section">
          <h2>About Binary Sudoku+</h2>
          <p className="app-detail-description">
            Binary Sudoku+ takes the classic Sudoku concept and adds a binary twist. Fill the grid with 0s and 1s 
            following two simple rules: no more than two identical digits in a row, and each row and column must 
            have an equal number of 0s and 1s. Simple to learn, deeply satisfying to master.
          </p>
        </section>

        <section className="app-detail-section">
          <h2>Features</h2>
          <div className="app-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="app-feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section">
          <h2>How to Play Binary Sudoku+</h2>
          <p className="app-detail-description">
            Fill every cell in the grid with either a 0 or a 1. Three constraints govern the placement: no more
            than two identical digits can sit next to each other in a row or column, each row and column must
            contain an equal number of 0s and 1s, and no two rows or columns can be identical. The "+" variant
            adds constraint symbols between certain cells - an equals sign means both cells share the same digit,
            while an X means they must differ.
          </p>
          <h3>Tips for New Players</h3>
          <ul className="app-detail-list">
            <li>If you see two identical digits in a row (like 0-0), the cells on either side must be 1.</li>
            <li>Count the digits in each line - once you have the maximum number of 0s or 1s, fill the rest.</li>
            <li>The "no identical rows" rule is useful late in a solve to distinguish between remaining possibilities.</li>
            <li>Constraint symbols (= and X) can create chains of deductions that resolve several cells at once.</li>
          </ul>
        </section>

        <section className="app-detail-section app-reviews-section">
          <h2>What Players Are Saying</h2>
          <div className="app-reviews-grid">
            {reviews.map((review, index) => (
              <div key={index} className="app-review-card">
                <div className="app-review-header">
                  <span className="app-review-author">{review.author}</span>
                  <span className="app-review-rating">{review.rating}</span>
                </div>
                <p className="app-review-text">"{review.text}"</p>
                <span className="app-review-source">{review.source}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section app-cta-section sp-cta">
          <div>
            <h2>Ready to Play?</h2>
            <p>Download Binary Sudoku+ and experience pure binary logic!</p>
          </div>
          <div className="app-cta-buttons sp-cta__actions">
            <Link to="/play/binarysudoku" className="button spark">
              Play Free Online
            </Link>
            <Link to="/apps" className="button sp-on-dark">
              ← Back to All Apps
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default BinarySudokuAppPage;
